/**
 * Single-message toast. Just enough surface for "X happened" confirmations
 * without pulling in a notification library. Mounts at the document root,
 * auto-dismisses after a few seconds, polite live region for a11y.
 */

import { useEffect, useState } from 'react';

import { Icon, type IconName } from './Icon.js';

import './Toast.css';

interface ToastMessage {
  id: number;
  text: string;
  kind: 'info' | 'success' | 'error';
}

let nextId = 1;
let listeners: ((msg: ToastMessage) => void)[] = [];

export function toast(text: string, kind: ToastMessage['kind'] = 'info'): void {
  const msg: ToastMessage = { id: nextId++, text, kind };
  for (const l of listeners) l(msg);
}

function ToastIcon({ kind }: { kind: ToastMessage['kind'] }): JSX.Element {
  const name: IconName =
    kind === 'success' ? 'check' : kind === 'error' ? 'alert-circle' : 'info-circle';
  // Success uses a slightly heavier stroke so the check shape reads at small size.
  const strokeWidth = kind === 'success' ? 2.4 : 2;
  return <Icon name={name} size={15} strokeWidth={strokeWidth} className="toast-icon" />;
}

export function ToastHost(): JSX.Element {
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  const dismiss = (id: number): void => setMessages((prev) => prev.filter((m) => m.id !== id));

  useEffect(() => {
    const onMessage = (msg: ToastMessage): void => {
      setMessages((prev) => [...prev, msg]);
      // Errors linger longer (easy to miss, usually actionable); everything
      // else clears after 4s. The user can also click to dismiss early.
      const ttl = msg.kind === 'error' ? 7000 : 4000;
      window.setTimeout(() => dismiss(msg.id), ttl);
    };
    listeners.push(onMessage);
    return () => {
      listeners = listeners.filter((l) => l !== onMessage);
    };
  }, []);

  return (
    <div className="toast-host" role="status" aria-live="polite">
      {messages.map((m) => (
        <div key={m.id} className={`toast toast-${m.kind}`}>
          <ToastIcon kind={m.kind} />
          <span className="toast-text">{m.text}</span>
          <button
            type="button"
            className="toast-close"
            onClick={() => dismiss(m.id)}
            aria-label="Dismiss"
            title="Dismiss"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
